import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatINR } from "@/lib/format";
import { CheckCircle2, Circle, GraduationCap } from "lucide-react";

export default function Expansion() {
  const { centers } = useApp();
  const [comparison, setComparison] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/expansion/comparison"), api.get("/strategies"),
      api.get("/milestones"), api.get("/batches"), api.get("/courses"),
    ]).then(([c, s, m, b, co]) => {
      setComparison(c.data); setStrategies(s.data); setMilestones(m.data); setBatches(b.data); setCourses(co.data);
    });
  }, []);

  const courseName = (id) => courses.find((c) => c.id === id)?.name || "";

  return (
    <div data-testid="expansion-page">
      <PageHeader eyebrow="Growth" title="Expansion & Profitability"
        subtitle="Unit P&L, break-even tracking, strategy boards, milestones and the Institute." />

      <Tabs defaultValue="comparison">
        <TabsList>
          <TabsTrigger value="comparison" data-testid="tab-comparison">Comparison</TabsTrigger>
          <TabsTrigger value="strategies" data-testid="tab-strategies">Strategies</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
          <TabsTrigger value="institute" data-testid="tab-institute">Institute</TabsTrigger>
        </TabsList>

        <TabsContent value="comparison" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {comparison.map((u) => (
              <Card key={u.center_id} className="p-5" data-testid={`unit-${u.center_name.split(" ")[0]}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-semibold text-sm">{u.center_name}</h3>
                  <Badge variant="secondary" className="text-[10px]">{u.type}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Revenue</p>
                <p className="font-display text-xl font-extrabold">{formatINR(u.revenue)}</p>
                <div className="mt-3 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Profit</span><span className={u.profit >= 0 ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{formatINR(u.profit)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Margin</span><span className="font-semibold">{u.margin}%</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Break-even</span><span className="font-semibold">{u.break_even_month}</span></div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="strategies" className="mt-4">
          <Card className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Strategy</TableHead><TableHead>Center</TableHead><TableHead>Owner</TableHead><TableHead>Budget</TableHead><TableHead>Status</TableHead><TableHead>Result</TableHead></TableRow></TableHeader>
              <TableBody>
                {strategies.map((s) => (
                  <TableRow key={s.id} data-testid={`strategy-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-xs">{s.center_code}</TableCell>
                    <TableCell className="text-xs">{s.owner}</TableCell>
                    <TableCell className="text-xs">{formatINR(s.budget)}</TableCell>
                    <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.result}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="milestones" className="mt-4">
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-4">Mathura launch timeline</h3>
            <div className="space-y-3">
              {milestones.map((m) => (
                <div key={m.id} className="flex items-center gap-3" data-testid={`milestone-${m.id}`}>
                  {m.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                  <div className="flex-1"><p className={`text-sm font-medium ${m.done ? "" : "text-muted-foreground"}`}>{m.name}</p></div>
                  <span className="text-xs text-muted-foreground">{m.date}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="institute" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {batches.map((b) => {
              const fill = Math.round((b.enrolled / (b.capacity || 1)) * 100);
              return (
                <Card key={b.id} className="p-5" data-testid={`batch-${b.id}`}>
                  <div className="flex items-center gap-2 mb-1"><GraduationCap className="h-4 w-4 text-primary" /><h3 className="font-display font-semibold">{b.name}</h3></div>
                  <p className="text-xs text-muted-foreground mb-3">{courseName(b.course_id)}</p>
                  <div className="flex justify-between text-sm mb-1"><span>Fill rate</span><span className="font-semibold">{b.enrolled}/{b.capacity} ({fill}%)</span></div>
                  <Progress value={fill} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-3">Placements: <span className="font-semibold text-foreground">{b.placement}</span></p>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
